import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Check, Loader2, AlertCircle } from 'lucide-react';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [searchParams] = useSearchParams();
  const isSignup = searchParams.get('signup') === 'true';

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await api.getSubscriptionPlans();
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subscription plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    console.log('handleSubscribe called with planId:', planId);
    
    // Verify we have an auth token
    const token = localStorage.getItem('access_token');
    console.log('Auth token exists:', !!token);
    if (!token) {
      console.error('No auth token found!');
      toast({
        title: 'Authentication Error',
        description: 'Please log in again to continue.',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }
    
    setProcessingPlan(planId);
    try {
      console.log('Creating checkout session...');
      const response = await api.createCheckoutSession(planId);
      console.log('Checkout session response:', response);
      
      if (response.url) {
        console.log('Redirecting to:', response.url);
        // Redirect to Stripe checkout
        window.location.href = response.url;
      } else {
        console.error('No URL in response:', response);
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start checkout process',
        variant: 'destructive'
      });
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Required Payment Notice for Signup */}
        {isSignup && (
          <Card className="mb-6 p-4 bg-yellow-50 border-yellow-300">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <div>
                <h3 className="font-semibold text-yellow-900">Payment Required to Complete Registration</h3>
                <p className="text-sm text-yellow-700">
                  Choose a plan to activate your account. You'll get a 7-day free trial - no charge until after the trial ends.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            {isSignup ? 'Complete Your Registration' : 'Choose Your Plan'}
          </h1>
          <p className="text-xl text-slate-600">
            Start with a 7-day free trial. Cancel anytime.
          </p>
        </div>

        {/* Payment Disclaimer */}
        <Card className="mb-8 p-6 bg-blue-50 border-blue-200 max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 text-lg mb-2">
                7-Day Free Trial - No Charge Until Trial Ends
              </h3>
              <p className="text-blue-800">
                Your card will be securely stored but <strong>not charged</strong> during your 7-day free trial. 
                You'll only be billed after the trial period ends. Cancel anytime before the trial ends to avoid any charges.
              </p>
            </div>
          </div>
        </Card>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`p-8 ${
                plan.id === 'yearly'
                  ? 'border-4 border-purple-500 shadow-xl relative'
                  : 'border-2 border-slate-200'
              }`}
            >
              {plan.id === 'yearly' && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Best Value
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-5xl font-bold text-purple-600">
                    ${plan.price}
                  </span>
                  <span className="text-slate-600">
                    /{plan.interval}
                  </span>
                </div>
                {plan.id === 'yearly' && (
                  <p className="text-green-600 font-semibold text-sm">
                    Get two months free!
                  </p>
                )}
                <p className="text-slate-500 text-sm mt-2">
                  {plan.description}
                </p>
              </div>

              <div className="space-y-3 mb-8">
                <Feature text="Complete homeschool tracking" />
                <Feature text="Multiple children support" />
                <Feature text="Custom lesson plans & assignments" />
                <Feature text="Grades & progress tracking" />
                <Feature text="Goal setting & rewards" />
                <Feature text="State requirements guidance" />
                <Feature text="7-day free trial included" />
                <Feature text="Cancel anytime" />
              </div>

              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={processingPlan === plan.id}
                className={`w-full py-6 text-lg font-semibold ${
                  plan.id === 'yearly'
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {processingPlan === plan.id ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Start 7-Day Free Trial`
                )}
              </Button>
              <p className="text-center text-xs text-slate-500 mt-3">
                No charge for 7 days. Cancel anytime.
              </p>
            </Card>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <FAQ
              question="When will I be charged?"
              answer="You won't be charged during your 7-day free trial. After the trial ends, you'll be automatically charged based on your selected plan (monthly or yearly). You can cancel anytime before the trial ends to avoid charges."
            />
            <FAQ
              question="Can I cancel my subscription?"
              answer="Yes! You can cancel your subscription at any time from your account settings. Your access will continue until the end of your billing period."
            />
            <FAQ
              question="What's included in the subscription?"
              answer="Full access to all features including unlimited children, lesson plans, assignments, grades tracking, goals, rewards, and state requirements guidance."
            />
            <FAQ
              question="Can I switch between monthly and yearly?"
              answer="Yes, you can upgrade or downgrade your plan at any time from your account settings. The change will take effect at your next billing cycle."
            />
          </div>
        </div>

        {/* Back Button */}
        {!isSignup && (
          <div className="text-center mt-12">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-slate-600"
            >
              ← Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const Feature = ({ text }) => (
  <div className="flex items-center gap-2">
    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
    <span className="text-slate-700">{text}</span>
  </div>
);

const FAQ = ({ question, answer }) => (
  <Card className="p-6">
    <h3 className="font-semibold text-slate-900 mb-2">{question}</h3>
    <p className="text-slate-600">{answer}</p>
  </Card>
);

export default SubscriptionPage;
